/**
 * The embed snippet: the HTML a host pastes into their own website.
 *
 * <p>One place that knows the markup shape, for the same reason {@code PublicLinks.java} is the
 * one place that knows public URL shapes. A snippet is worse than a URL to get wrong: it is
 * copied into pages we cannot edit, on sites we do not control, and it keeps working (or keeps
 * failing) long after anybody remembers where it came from. Two builders drifting apart would
 * mean two different contracts in the wild with no way to tell which a given page is running.</p>
 *
 * <p>Inline only. Popup and badge embeds are deliberately not offered in v1.</p>
 */

/** Path the loader is served from. Pasted into third-party pages, so it never moves. */
export const EMBED_LOADER_PATH = "/embed.js";

/** Placeholder height before the page reports its real one. A month of slots is about this tall. */
export const DEFAULT_EMBED_HEIGHT = 700;

/**
 * Below this the calendar has nowhere to go. Set on the container rather than the iframe so a
 * narrow column on the host's page cannot squeeze the widget into an unusable sliver.
 */
export const MIN_EMBED_WIDTH = 320;

export interface EmbedSnippetOptions {
  /** Absolute URL of the public page, e.g. {@code https://scheda.delgentic.com/@chris/intro}. */
  url: string;
  /** Drop the banner, avatar and host name. */
  hideHeader?: boolean;
  /** Drop the "what you're booking" summary, keeping the picker. */
  hideDetails?: boolean;
  /** Starting height in pixels. Auto-height corrects this as soon as the page loads. */
  height?: number;
}

/**
 * Escape for an HTML attribute delimited by double quotes.
 *
 * <p>{@code &} first, or the ampersands introduced by the later replacements get escaped a second
 * time and the host pastes {@code &amp;amp;} into their page.</p>
 */
function attr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * The URL the widget points at: the public link plus the flags the host chose.
 *
 * <p>{@code embed=1} is included even though the loader sets it too. It makes the URL meaningful
 * on its own, so a host who prefers a plain {@code <iframe>} to our script still gets the embedded
 * rendering rather than a full-page layout inside their box. They lose auto-height, which is a
 * fair trade for not running our JavaScript, and it degrades to exactly what Calendly does by
 * default.</p>
 */
export function embedUrl(options: EmbedSnippetOptions): string {
  const url = new URL(options.url);
  url.searchParams.set("embed", "1");
  if (options.hideHeader) url.searchParams.set("hide_header", "1");
  if (options.hideDetails) url.searchParams.set("hide_details", "1");
  return url.toString();
}

/** Absolute URL of the loader, taken from the link's own origin so the two can never disagree. */
export function loaderUrl(pageUrl: string): string {
  return new URL(EMBED_LOADER_PATH, pageUrl).toString();
}

/**
 * The full paste-able snippet.
 *
 * <p>{@code async} on the script because nothing on the host's page waits for it, and a booking
 * widget has no business blocking somebody else's page render.</p>
 */
export function embedSnippet(options: EmbedSnippetOptions): string {
  const height = options.height ?? DEFAULT_EMBED_HEIGHT;
  return (
    `<div class="scheda-inline-widget"\n` +
    `     data-url="${attr(embedUrl(options))}"\n` +
    `     style="min-width:${MIN_EMBED_WIDTH}px;height:${height}px"></div>\n` +
    `<script src="${attr(loaderUrl(options.url))}" async></script>`
  );
}
